'use client'

import clsx from 'clsx'

import { createContext, type JSX, type MouseEvent, type ReactNode, useCallback, useEffect, useRef } from 'react'

import { createHalo, type Halo } from './hero-halo-renderer'
import useReducedMotion from './use-reduced-motion'

/** Sends one strong wave from the halo's centre. A no-op outside a `<HeroHalo>`. */
export const HaloPulse = createContext<() => void>(() => undefined)

type HeroHaloProps = { children: ReactNode; className?: string }

/**
 * The home hero's particle halo, drawn behind `children`. The canvas fills this element, so the
 * element is the halo's box: on production that is the full hero width by the content's height, with
 * the rings centred at 78% x 42% (74% x 27% below 700px). The drawing lives in `hero-halo-renderer.ts`;
 * this component only mounts it, recreates it when reduced motion flips, and forwards clicks and the
 * rotator's pulse (through `HaloPulse`) to it.
 *
 * The colour is the canvas's `color`, which the renderer reads once: `accent`, in both themes. The two
 * left-edge masks are production's, measured
 * at 1728px and 390px (2026-09-23); the second applies from its `max-width: 760px`.
 */
export default function HeroHalo({ children, className }: HeroHaloProps): JSX.Element {
  const canvas = useRef<HTMLCanvasElement>(null)
  const halo = useRef<Halo | null>(null)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    if (!canvas.current) return undefined
    const created = createHalo(canvas.current, { reducedMotion })
    halo.current = created

    return (): void => {
      created?.dispose()
      halo.current = null
    }
  }, [reducedMotion])

  const pulse = useCallback((): void => halo.current?.pulse(), [])

  // Pointer clicks only, as on production: `detail` is 0 for a click synthesised by the keyboard.
  const burst = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.detail > 0) halo.current?.burst(event.clientX, event.clientY)
  }

  return (
    // A decorative flourish on top of whatever was clicked, not a control: there is nothing to offer
    // keyboard or assistive-tech users, and the click still reaches its real target.
    <div onClick={burst} className={clsx('relative', className)}>
      <canvas
        ref={canvas}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 block size-full text-accent [mask-image:linear-gradient(90deg,transparent_0%,rgba(0,0,0,0.56)_20%,#000_48%,#000_100%)] max-[761px]:[mask-image:linear-gradient(90deg,transparent_0%,rgba(0,0,0,0.38)_18%,#000_50%,#000_100%)] print:hidden dark:[--halo-boost:1.8]"
      />
      <div className="relative">
        <HaloPulse value={pulse}>{children}</HaloPulse>
      </div>
    </div>
  )
}
